import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
  Alert, CircularProgress, MenuItem, Chip, InputAdornment,
  Tooltip, useTheme
} from '@mui/material';
import { Add, Edit, Delete, Search, Storefront } from '@mui/icons-material';
import api from '../../api/axios';

// ─── Tasarım sabitleri ────────────────────────────────────────────────────────
const BRAND  = '#1A3A6B';
const ACCENT = '#c0392b';

interface CompetitorPrice {
  id: number;
  cableFamily: string;
  spec: string;
  competitorName: string;
  listPriceTl: number | null;
  discountRate: number;
  netPriceTl: number | null;
  notes: string;
  active: boolean;
}

const FAMILIES = ['NAYY', 'NA2XY', 'NA2XH', 'AER', 'AAC', 'ACSR'];

const empty: Partial<CompetitorPrice> = {
  cableFamily: 'NAYY', spec: '', competitorName: '',
  listPriceTl: null, discountRate: 0, netPriceTl: null, notes: '', active: true,
};

const familyColor: Record<string, string> = {
  NAYY: '#D97706', NA2XY: '#0284C7', NA2XH: '#7C3AED',
  AER: '#B45309', AAC: '#16A34A', ACSR: '#1A3A6B'
};

const actionBtn = (BORDER: string) => ({
  width: 30, height: 30, borderRadius: '6px',
  border: `1px solid ${BORDER}`, color: '#64748b', bgcolor: '#fff',
});

const CompetitorPricesPage: React.FC = () => {
  const theme   = useTheme();
  const BORDER  = theme.palette.divider;
  const SURFACE = theme.palette.background.paper;
  const TEXT2   = theme.palette.text.secondary;

  const [prices, setPrices] = useState<CompetitorPrice[]>([]);
  const [competitors, setCompetitors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CompetitorPrice | null>(null);
  const [form, setForm] = useState<Partial<CompetitorPrice>>(empty);
  const [filterFamily, setFilterFamily] = useState('');
  const [filterComp, setFilterComp] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const [pricesRes, metaRes] = await Promise.all([
        api.get('/competitor-prices'),
        api.get('/competitor-prices/meta'),
      ]);
      setPrices(pricesRes.data);
      setCompetitors(metaRes.data.competitors || []);
    } catch {
      setError('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleListPriceChange = (val: number) => {
    const net = val * (1 - (form.discountRate || 0));
    setForm({ ...form, listPriceTl: val, netPriceTl: parseFloat(net.toFixed(2)) });
  };

  const handleDiscountChange = (val: number) => {
    const net = (form.listPriceTl || 0) * (1 - val);
    setForm({ ...form, discountRate: val, netPriceTl: parseFloat(net.toFixed(2)) });
  };

  const filtered = useMemo(() => prices.filter(p => {
    const fam = !filterFamily || p.cableFamily === filterFamily;
    const comp = !filterComp || p.competitorName === filterComp;
    const src = !search || `${p.cableFamily} ${p.spec}`.toLowerCase().includes(search.toLowerCase());
    return fam && comp && src;
  }), [prices, filterFamily, filterComp, search]);

  const handleOpen = (price?: CompetitorPrice) => {
    setEditing(price || null);
    setForm(price ? { ...price } : { ...empty });
    setOpen(true);
    setError('');
  };

  const handleSave = async () => {
    if (!form.cableFamily || !form.spec || !form.competitorName) return;
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/competitor-prices/${editing.id}`, form);
      } else {
        await api.post('/competitor-prices', form);
      }
      setOpen(false);
      load();
    } catch {
      setError('Kayıt sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/competitor-prices/${id}`);
      load();
    } catch {
      setError('Silme sırasında hata oluştu');
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
            Rakip Fiyatları Yönetimi
          </Typography>
          <Typography sx={{ fontSize: '13px', color: TEXT2, mt: 0.5 }}>
            Rakip firma fiyatlarını ekle, güncelle ve yönet
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}
                sx={{ bgcolor: ACCENT, fontWeight: 600, borderRadius: '8px',
                  px: 2.5, fontSize: '13px', boxShadow: 'none', height: 38,
                  '&:hover': { bgcolor: '#a93226', boxShadow: 'none' } }}>
          Yeni Fiyat Ekle
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── Filtreler ── */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: `1px solid ${BORDER}`, borderRadius: '10px', bgcolor: SURFACE }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Kesit ara..." value={search}
                     onChange={e => setSearch(e.target.value)}
                     InputProps={{
                       startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: TEXT2 }} /></InputAdornment>,
                       sx: { borderRadius: '8px', fontSize: '13px', minWidth: 180 },
                     }}
                     sx={{ '& fieldset': { borderColor: BORDER } }} />

          <Box sx={{ width: 1, height: 24, bgcolor: BORDER, mx: 0.5 }} />

          <Typography sx={{ fontSize: '12px', color: TEXT2, fontWeight: 600 }}>Aile:</Typography>
          <Chip label="Tümü" size="small" onClick={() => setFilterFamily('')}
                sx={{
                  height: 22, fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                  bgcolor: !filterFamily ? '#0A1628' : 'transparent',
                  color: !filterFamily ? 'white' : '#0A1628',
                  border: '1.5px solid #0A1628',
                }} />
          {FAMILIES.map(f => {
            const count = prices.filter(p => p.cableFamily === f).length;
            if (count === 0) return null;
            const isSelected = filterFamily === f;
            return (
              <Chip key={f} label={`${f} (${count})`} size="small"
                    onClick={() => setFilterFamily(isSelected ? '' : f)}
                    sx={{
                      height: 22, fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                      bgcolor: isSelected ? familyColor[f] : 'transparent',
                      color: isSelected ? 'white' : familyColor[f],
                      border: `1.5px solid ${familyColor[f]}`,
                      '&:hover': { opacity: 0.85 }
                    }} />
            );
          })}

          <TextField select size="small" label="Firma" value={filterComp}
                     onChange={e => setFilterComp(e.target.value)}
                     sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
            <MenuItem value="">Tümü</MenuItem>
            {competitors.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>

          <Typography sx={{ fontSize: '12px', color: TEXT2, ml: 'auto' }}>
            <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>{filtered.length}</Box>
            <Box component="span" sx={{ color: TEXT2 }}> / {prices.length} kayıt</Box>
          </Typography>
        </Box>
      </Paper>

      {/* ── Tablo ── */}
      {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress sx={{ color: BRAND }} />
          </Box>
      ) : (
          <TableContainer component={Paper} elevation={0}
                          sx={{ borderRadius: '10px', border: `1px solid ${BORDER}`, overflow: 'hidden', bgcolor: SURFACE }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Kablo Ailesi','Kesit','Firma','Liste Fiyatı (₺)','İskonto','Net Fiyat (₺)','Not','İşlem'].map(h => (
                      <TableCell key={h}
                                 align={['Liste Fiyatı (₺)','Net Fiyat (₺)'].includes(h) ? 'right' : (['İskonto','İşlem'].includes(h) ? 'center' : 'left')}
                                 style={{ background: '#f8f9fc', paddingTop: 12, paddingBottom: 12,
                                   fontSize: 11, fontWeight: 600, color: '#8a92a6',
                                   textTransform: 'uppercase', letterSpacing: '0.05em',
                                   borderBottom: '1px solid #e8eaf0' }}>
                        {h}
                      </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8, color: TEXT2 }}>
                        <Typography sx={{ fontSize: '14px' }}>Kayıt bulunamadı</Typography>
                      </TableCell>
                    </TableRow>
                ) : (
                    filtered.map(p => (
                        <TableRow key={p.id}
                                  sx={{
                                    transition: 'background .1s',
                                    '& td': { borderBottom: '1px solid #e8eaf0', py: 0 },
                                    '&:hover td': { bgcolor: '#fafbfe !important' },
                                  }}>
                          <TableCell sx={{ height: 48 }}>
                            <Chip label={p.cableFamily} size="small"
                                  sx={{
                                    height: 22, fontSize: '11px', fontWeight: 700, borderRadius: '6px',
                                    bgcolor: familyColor[p.cableFamily] || '#64748B', color: 'white',
                                  }} />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: BRAND, fontFamily: 'monospace' }}>
                              {p.spec}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={p.competitorName} size="small"
                                  sx={{
                                    height: 22, fontSize: '11px', fontWeight: 700, borderRadius: '6px',
                                    bgcolor: p.competitorName === 'KLC' ? BRAND : '#64748B',
                                    color: 'white'
                                  }} />
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: '12px', fontFamily: 'monospace',
                              color: p.listPriceTl != null ? theme.palette.text.primary : 'text.disabled' }}>
                              {p.listPriceTl != null ? `${p.listPriceTl.toFixed(2)} ₺` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {p.discountRate > 0 ? (
                                <Chip label={`-%${(p.discountRate * 100).toFixed(0)}`} size="small"
                                      sx={{ height: 22, bgcolor: '#FEF3C7', color: '#B45309',
                                        fontWeight: 700, fontSize: '11px', borderRadius: '6px',
                                        border: '1px solid #fde68a' }} />
                            ) : (
                                <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>%0</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{
                              fontSize: '13px', fontWeight: 700, fontFamily: 'monospace',
                              color: p.netPriceTl != null ? '#16A34A' : 'text.disabled'
                            }}>
                              {p.netPriceTl != null ? `${p.netPriceTl.toFixed(2)} ₺` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '11px', color: TEXT2 }}>{p.notes || '—'}</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ pr: 2 }}>
                            <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <Tooltip title="Düzenle">
                                <IconButton size="small" onClick={() => handleOpen(p)}
                                            style={actionBtn(BORDER)}
                                            sx={{ '&:hover': { background: '#fff8ed !important', color: '#d97706 !important', borderColor: '#fed7aa !important' } }}>
                                  <Edit sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sil">
                                <IconButton size="small" onClick={() => handleDelete(p.id)}
                                            style={actionBtn(BORDER)}
                                            sx={{ '&:hover': { background: '#fff0f0 !important', color: '#dc2626 !important', borderColor: '#fca5a5 !important' } }}>
                                  <Delete sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
      )}

      {/* ── Edit Dialog ── */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth
              PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <DialogTitle sx={{ bgcolor: BRAND, color: '#fff', py: 2, px: 3, fontSize: '15px', fontWeight: 600 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Storefront fontSize="small" />
            {editing ? 'Fiyat Düzenle' : 'Yeni Fiyat Ekle'}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField select label="Kablo Ailesi" value={form.cableFamily || 'NAYY'} size="small"
                         onChange={e => setForm({ ...form, cableFamily: e.target.value })} fullWidth>
                {FAMILIES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>
              <TextField label="Kesit *" value={form.spec || ''} size="small"
                         onChange={e => setForm({ ...form, spec: e.target.value })}
                         placeholder="1x10, 3x50+35..." fullWidth />
            </Box>
            <TextField label="Firma Adı *" value={form.competitorName || ''} size="small"
                       onChange={e => setForm({ ...form, competitorName: e.target.value })}
                       placeholder="HASÇELİK, ÖZNUR, KLC..." fullWidth />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Liste Fiyatı (₺)" type="number" size="small"
                         value={form.listPriceTl ?? ''}
                         onChange={e => handleListPriceChange(parseFloat(e.target.value) || 0)}
                         slotProps={{ input: { endAdornment: <InputAdornment position="end">₺/m</InputAdornment> } }}
                         fullWidth />
              <TextField label="İskonto" type="number" size="small"
                         value={form.discountRate != null ? (form.discountRate * 100).toFixed(0) : ''}
                         onChange={e => handleDiscountChange(parseFloat(e.target.value) / 100 || 0)}
                         slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
                         fullWidth />
            </Box>
            <TextField label="Net Fiyat (₺)" type="number" size="small"
                       value={form.netPriceTl ?? ''}
                       onChange={e => setForm({ ...form, netPriceTl: parseFloat(e.target.value) || null })}
                       slotProps={{ input: { endAdornment: <InputAdornment position="end">₺/m</InputAdornment> } }}
                       helperText="Liste fiyatı × (1 - iskonto) olarak otomatik hesaplanır"
                       fullWidth />
            <TextField label="Not" value={form.notes || ''} size="small"
                       onChange={e => setForm({ ...form, notes: e.target.value })}
                       placeholder="Fiyat listesi tarihi, dönem vb." fullWidth />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setOpen(false)} sx={{ borderRadius: '8px', color: TEXT2, textTransform: 'none' }}>
            İptal
          </Button>
          <Button variant="contained" onClick={handleSave}
                  disabled={saving || !form.spec || !form.competitorName}
                  sx={{ bgcolor: BRAND, borderRadius: '8px', fontWeight: 600, textTransform: 'none', px: 3, boxShadow: 'none',
                    '&:hover': { bgcolor: '#0f2a52', boxShadow: 'none' } }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompetitorPricesPage;
